// Copyright (c) StrangeBee 2024

import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import client from '@Client';
import { useLocalMessage } from '@Helpers/locale';
import { useDrawer } from '@Hooks/useDrawer';
import useListQuery from '@Hooks/useListQuery';
import useValidateForm from '@Hooks/useValidateForm';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { THForm } from '@Molecules/THForm/THForm';
import { CaseCreateSharingManage } from '@Organisms/Case/CaseCreate/CaseCreateSharingManage';
import { computeListSelector } from '@Organisms/Organisation/selector';
import { computeListSelector as computeProfileListSelector } from '@Organisms/Profile/selector';
import { useGenericQuery } from '@Queries/genericQueries';
import { useCurrentUser } from '@Queries/userQueries';
import { Space } from 'antd';
import { FormInstance } from 'antd/lib/form';
import React, { ReactElement, useMemo } from 'react';
import {
    InputShare,
    SharingProfileOutput,
    SharingRule,
    SharingRuleValues,
    OrganisationOutput,
    ProfileOutput,
    SharedOrgOutput,
} from 'thehive-sdk';

import './CaseShareAdd.scss';
import { getAvailableLinks } from '@Helpers/caseSharing';

interface CaseShareFormValues {
    taskRule: SharingRule;
    observableRule: SharingRule;
    profile: Record<string, string>;
    sharingParameters?: (InputShare & { editable?: boolean; isShared?: boolean })[];
}

interface CaseShareFormProps {
    form: FormInstance;
    isValid: boolean;
    onFinish: (values: CaseShareFormValues) => void;
    onValuesChange: () => void;
    shareList: SharedOrgOutput[];
    currentOrganisation?: string;
    onCancel: () => void;
}

const CaseShareForm = ({
    form,
    isValid,
    onFinish,
    onValuesChange,
    shareList,
    currentOrganisation,
    onCancel,
}: CaseShareFormProps): ReactElement => {
    const i = useLocalMessage();

    const { data: currentUser } = useCurrentUser();
    const { visible, setVisible } = useDrawer({});

    const ownShare = useMemo(
        () => shareList.find(({ organisationName }) => organisationName === currentOrganisation),
        [shareList, currentOrganisation],
    );

    const { data: sharingProfileList = [] } = useGenericQuery<SharingProfileOutput[]>({
        fetchCallback: () => client.OrganisationController.getSharingProfile(),
        key: ['get-organisation-sharing-profile'],
        manual: true,
        fetchOnStart: true,
    });

    const { data: orgList = [] } = useListQuery<OrganisationOutput[]>({
        queryParams: {
            selector: computeListSelector(),
        },
        controller: client.OrganisationController,
        manual: true,
        fetchOnStart: true,
    });

    const currentOrg = orgList?.find((org) => org.name === currentUser?.organisation);

    const { data: profileList = [] } = useListQuery<ProfileOutput[]>({
        queryParams: {
            selector: computeProfileListSelector(),
        },
        controller: client.ProfileController,
        manual: true,
        fetchOnStart: true,
    });

    const orgProfileList = useMemo(() => profileList.filter((profile) => profile.forOrg), [profileList]);

    const availableLinks = useMemo(
        () => currentOrg?.links && getAvailableLinks(currentOrg.links, orgList),
        [currentOrg?.links, orgList],
    );

    return (
        <THForm<CaseShareFormValues>
            name="create-case-share-form"
            onFinish={(values) => {
                onFinish(values);
            }}
            form={form}
            onValuesChange={onValuesChange}
            initialValues={{
                taskRule: ownShare?.taskRule || currentOrg?.taskRule,
                observableRule: ownShare?.observableRule || currentOrg?.observableRule,
                sharingParameters:
                    availableLinks?.map(({ toOrganisation = '', linkType = '' }) => {
                        const sharingProfile = sharingProfileList.find(({ name }) => name === linkType);

                        const share = shareList?.find(({ organisationName }) => organisationName === toOrganisation);

                        return {
                            organisation: toOrganisation,
                            profile: share?.profileName || sharingProfile?.permissionProfile,
                            isShared: !!share,
                            observableRule: sharingProfile?.observableRule,
                            taskRule: sharingProfile?.taskRule,
                            editable: sharingProfile?.editable,
                        };
                    }) || [],
            }}
        >
            <THForm.Layout footer={<FormSubmitButton onCancel={onCancel} disabled={!isValid} />}>
                <THForm.Item
                    label={i('sharing.with')}
                    className="sharing-input"
                    extraAction={
                        <THButton type="link" onClick={() => setVisible(true)}>
                            {i('case.actions.share.manage')}
                        </THButton>
                    }
                >
                    <THForm.List name="sharingParameters">
                        {(fields) => (
                            <CaseCreateSharingManage
                                fields={fields}
                                orgList={orgList}
                                orgProfileList={orgProfileList}
                                form={form}
                                visible={visible}
                                onVisibleChange={setVisible}
                            />
                        )}
                    </THForm.List>
                </THForm.Item>
                <THForm.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                        const orgShared = (
                            getFieldValue('sharingParameters') as (InputShare & {
                                isShared?: boolean;
                            })[]
                        ).filter(({ isShared }) => isShared);

                        return (
                            <div className="case-create-form__sharings">
                                {!orgShared.length && (
                                    <p className="case-create-form__sharings__helper">
                                        {i('case.actions.share.helper-none')}
                                    </p>
                                )}

                                <div className="case-create-form__sharings__org-list">
                                    {orgShared?.map((value) => {
                                        const { organisation, profile } = value;
                                        const org = orgList.find(({ name }) => name === organisation);

                                        return (
                                            org && (
                                                <div
                                                    key={org.name}
                                                    className="case-create-form__sharings__org-list-item"
                                                >
                                                    <Space>
                                                        <UserAvatar avatar={org.avatar} />
                                                        <span>
                                                            {org.name} ({profile})
                                                        </span>
                                                    </Space>
                                                </div>
                                            )
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    }}
                </THForm.Item>
                <THForm.Item<any> noStyle shouldUpdate={(prev, next) => prev.taskRule !== next.taskRule}>
                    {({ getFieldValue }) => (
                        <THForm.Item
                            name="taskRule"
                            label={i('organisation.create.form.task-rule.label')}
                            help={i(`case.create.form.sharing.help.taskRule.${getFieldValue(['taskRule'])}`, {
                                name: currentOrg?.name || '',
                            })}
                        >
                            <THSelect
                                options={SharingRuleValues.map((value) => ({ label: value, value }))}
                                placeholder={i('organisation.create.form.task-rule.placeholder')}
                            />
                        </THForm.Item>
                    )}
                </THForm.Item>

                <THForm.Item<any> noStyle shouldUpdate={(prev, next) => prev.observableRule !== next.observableRule}>
                    {({ getFieldValue }) => (
                        <THForm.Item
                            name="observableRule"
                            label={i('organisation.create.form.observable-rule.label')}
                            help={i(
                                `case.create.form.sharing.help.observableRule.${getFieldValue(['observableRule'])}`,
                                { name: currentOrg?.name || '' },
                            )}
                        >
                            <THSelect
                                options={SharingRuleValues.map((value) => ({ label: value, value }))}
                                placeholder={i('organisation.create.form.observable-rule.placeholder')}
                            />
                        </THForm.Item>
                    )}
                </THForm.Item>
            </THForm.Layout>
        </THForm>
    );
};

interface CaseShareAddProps {
    onAddShare: (values: CaseShareFormValues, caseId: string) => void;
    caseId: string;
    triggerElem: ReactElement;
    shareList: SharedOrgOutput[];
    currentOrganisation?: string;
}

export const CaseShareAdd = ({
    onAddShare,
    caseId,
    triggerElem,
    shareList,
    currentOrganisation,
}: CaseShareAddProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const onFinish = (values: CaseShareFormValues) => {
        onAddShare(values, caseId);
    };

    return (
        <THDrawer
            title={i('sharing.title')}
            className="drawer-form"
            triggerElem={triggerElem}
            afterVisibleChange={() => {
                form.resetFields();
            }}
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) => (
                <CaseShareForm
                    form={form}
                    isValid={isValid && fieldsTouched}
                    onFinish={({ taskRule, observableRule, profile, sharingParameters }: CaseShareFormValues) => {
                        onFinish({ taskRule, observableRule, profile, sharingParameters });
                        toggleDrawer();
                        form.resetFields();
                        setFieldsTouched(false);
                    }}
                    onValuesChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    onCancel={() => {
                        setFieldsTouched(false);
                        form.resetFields();
                        toggleDrawer();
                    }}
                    shareList={shareList}
                    currentOrganisation={currentOrganisation}
                />
            )}
        </THDrawer>
    );
};
